import { Pencil } from "lucide-react";
import { useState } from "react";
import { PersonSelfContactDialog } from "@/components/persons/PersonSelfContactDialog";
import type { Adorn } from "@/components/persons/person-profile/use-pending-adornments";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { Button } from "@/components/ui/button";
import type { PersonDetail } from "@/hooks/use-persons";
import { useWorkLocationOptions } from "@/hooks/use-work-locations";
import { formatClock } from "@/lib/working-hours";

/** "09:00[:00]"–"17:30[:00]" → "09:00–17:30" / "From 09:00" / "Until 17:30" / null. */
function formatRange(start: string | null, end: string | null): string | null {
  if (start && end) return `${formatClock(start)}–${formatClock(end)}`;
  if (start) return `From ${formatClock(start)}`;
  if (end) return `Until ${formatClock(end)}`;
  return null;
}

/**
 * Working hours, always in U.S. Eastern Time (ET) — the "(ET)" lives on the row
 * label, not each time. Stacks an optional second block for split shifts (e.g.
 * "09:00–12:00" / "15:00–19:00"). Renders "—" when no hours are set.
 */
function WorkingHoursValue({ person }: { person: PersonDetail }) {
  const ranges = [
    formatRange(person.workingHoursStart, person.workingHoursEnd),
    formatRange(person.workingHoursStart2, person.workingHoursEnd2),
  ].filter((r): r is string => r !== null);
  if (ranges.length === 0) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="flex flex-col">
      {ranges.map((r) => (
        <span key={r}>{r}</span>
      ))}
    </div>
  );
}

export function ContactInfoCard({
  person,
  adorn,
  canView,
  isSelf,
}: {
  person: PersonDetail;
  adorn: Adorn;
  canView: boolean;
  isSelf: boolean;
}) {
  const [editing, setEditing] = useState(false);
  // Work location is admin-managed config, so its label comes from that table.
  // `labelFor` falls back to the raw token, which is what a value retired (or
  // deleted) after this person was set should read as — better than a blank.
  const locations = useWorkLocationOptions();

  return (
    <DetailSection
      title="Contact Info"
      boldTitle
      className="gap-3 py-4"
      headerActions={
        // Only the person themselves can self-edit these fields (immediate, no
        // effective date). Admins use the full edit form instead.
        isSelf ? (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-1 size-3.5" />
            Edit
          </Button>
        ) : undefined
      }
    >
      {isSelf && (
        <PersonSelfContactDialog open={editing} onOpenChange={setEditing} person={person} />
      )}
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <MetadataRow label="Work email" value={person.email} adornment={adorn("email")} />
        {/* Personal contact details are sensitive: the read tier sees them on
            any profile, everyone else only on their own. The server redacts
            both to null for anyone else — this is the second layer, keeping an
            empty row (and its queued-change icon) off a profile where the
            value could not be shown anyway. */}
        {(canView || isSelf) && (
          <MetadataRow
            label="Personal email"
            value={person.personalEmail}
            adornment={adorn("personalEmail")}
          />
        )}
        {(canView || isSelf) && (
          <MetadataRow label="Phone" value={person.phone} adornment={adorn("phone")} />
        )}
        <MetadataRow
          label="Location"
          adornment={adorn("location")}
          value={person.location ? locations.labelFor(person.location) : null}
        />
        <MetadataRow
          label="Hours / week"
          adornment={adorn("hoursPerWeek")}
          value={person.hoursPerWeek != null ? String(person.hoursPerWeek) : null}
        />
        <MetadataRow
          label="Working hours (ET)"
          adornment={adorn(
            "workingHoursStart",
            "workingHoursEnd",
            "workingHoursStart2",
            "workingHoursEnd2",
          )}
        >
          <WorkingHoursValue person={person} />
        </MetadataRow>
        {/* Only shown when a note exists (WI request) — the free-text caveat is
            the exception, not something to render an em-dash for. */}
        {person.workingHoursNote && (
          <MetadataRow
            label="Working hours note"
            value={person.workingHoursNote}
            adornment={adorn("workingHoursNote")}
          />
        )}
      </div>
    </DetailSection>
  );
}
